import {Component, Inject, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {Broadcast} from "../../model/broadcast/broadcast";
import {BroadcastsService} from "../../services/broadcasts.service";
import {UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule} from "@angular/forms";
import "firebase/compat/firestore";
import {Player} from "../../model/broadcast/player";
import {UtilService} from "../../services/util.service";
import {MAT_DIALOG_DATA, MatDialogRef, MatDialogModule} from "@angular/material/dialog";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {MatTabsModule} from "@angular/material/tabs";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatCardModule} from "@angular/material/card";
import {MatRadioModule} from "@angular/material/radio";
import {MatIconModule} from "@angular/material/icon";
import {MatSelectModule} from "@angular/material/select";
import {NgxColorsModule} from "ngx-colors";
import {TipsportService} from "../../services/tipsport.service";
import {TipsportCompetition, TipsportMatch} from "../../model/broadcast/tipsport";

@Component({
    selector: 'app-config-dialog',
    templateUrl: './config-dialog.component.html',
    styleUrls: ['./config-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatTabsModule,
        MatCheckboxModule,
        MatCardModule,
        MatRadioModule,
        MatIconModule,
        MatSelectModule,
        NgxColorsModule,
        ReactiveFormsModule
    ]
})
export class ConfigDialogComponent implements OnInit {

  formData: UntypedFormGroup;

  tipsportCompetitions: TipsportCompetition[] = [];
  tipsportMatches: TipsportMatch[] = [];

  colorPalette: Array<any> = [
    '#020202',
    '#0d0e28',
    '#041c2c',
    '#041e42',
    '#142c48',
    '#183883',
    '#193c90',
    '#214997',
    '#03483a',
    '#b31e2a',
    '#D50032',
    '#f48117',
    '#dddd00',
    '#ffffff',
  ];

  private SAMPLE_BATTER: Player = {
    name: "Current batter", hrDerby: 0, guid: '', batting: true
  };

  constructor(
    public dialogRef: MatDialogRef<ConfigDialogComponent>,
    public broadcastsService: BroadcastsService,
    public utilService: UtilService,
    private tipsportService: TipsportService,
    @Inject(MAT_DIALOG_DATA) public broadcast: Broadcast,
  ) {
  }

  ngOnInit(): void {
    this.formData = new UntypedFormGroup({
      statusMsg: new UntypedFormControl(this.broadcast.config.statusMsg),
      bigLogoUrl: new UntypedFormControl(this.broadcast.config.bigLogoUrl),
      smallLogoUrl: new UntypedFormControl(this.broadcast.config.smallLogoUrl),
      backgroundColor: new UntypedFormControl(this.broadcast.config.backgroundColor),
      autopilot: new UntypedFormControl(this.broadcast.config.autopilot),
      skipPortraits: new UntypedFormControl(this.broadcast.config.skipPortraits),
      manualStatus: new UntypedFormControl(this.broadcast.config.manualStatus),
      useManualLineups: new UntypedFormControl(this.broadcast.config.useManualLineups),
      unites: new UntypedFormControl(this.broadcast.config.unites),
      lowerRange: new UntypedFormControl(this.broadcast.config.lowerRange),
      boxScoreTime: new UntypedFormControl(this.broadcast.config.boxScoreTime),
      batterTime: new UntypedFormControl(this.broadcast.config.batterTime),
      tipsportCompetitionId: new UntypedFormControl(this.broadcast.config.tipsportCompetitionId ?? null),
      tipsportMatchId: new UntypedFormControl(this.broadcast.config.tipsportMatchId ?? null),
      awayConfig: new UntypedFormGroup({
        fullName: new UntypedFormControl(this.broadcast.config.awayConfig.fullName, [Validators.required]),
        // Bez required: barva týmu je nepovinná a u týmu z autodiscovery chybí,
        // takže by required zablokoval uložení celého nastavení vysílání jen
        // proto, že liga barvy nemá. Prázdná se vykreslí jako neutrál skinu.
        primaryColor: new UntypedFormControl(this.broadcast.config.awayConfig.primaryColor),
        secondaryColor: new UntypedFormControl(this.broadcast.config.awayConfig.secondaryColor),
        logoUrl: new UntypedFormControl(this.broadcast.config.awayConfig.logoUrl),
      }),
      homeConfig: new UntypedFormGroup({
        fullName: new UntypedFormControl(this.broadcast.config.homeConfig.fullName, [Validators.required]),
        primaryColor: new UntypedFormControl(this.broadcast.config.homeConfig.primaryColor),
        secondaryColor: new UntypedFormControl(this.broadcast.config.homeConfig.secondaryColor),
        logoUrl: new UntypedFormControl(this.broadcast.config.homeConfig.logoUrl),
      }),
    });
    if (!this.broadcast.leagueId) {
      this.formData.controls.autopilot.disable();
    }

    // Load the Tipsport competition list; if a competition is already configured,
    // load its matches so the match dropdown is populated on open.
    this.tipsportService.getCompetitions().subscribe(competitions => {
      this.tipsportCompetitions = competitions || [];
    });
    if (this.broadcast.config.tipsportCompetitionId) {
      this.loadTipsportMatches(this.broadcast.config.tipsportCompetitionId);
    }
  }

  onTipsportCompetitionChange(competitionId: string): void {
    // A different competition invalidates the previously picked match.
    this.formData.controls.tipsportMatchId.setValue(null);
    this.tipsportMatches = [];
    if (competitionId) {
      this.loadTipsportMatches(competitionId);
    }
  }

  private loadTipsportMatches(competitionId: string): void {
    this.tipsportService.getMatches(competitionId).subscribe(matches => {
      this.tipsportMatches = matches || [];
    });
  }

  close(): void {
    this.dialogRef.close(this.broadcast.config);
  }

  update(): void {
    const old = this.broadcast.config;
    this.broadcast.config = this.formData.value;
    this.broadcast.config.homeConfig.id = old.homeConfig.id;
    this.broadcast.config.homeConfig.shortName = old.homeConfig.shortName;
    this.broadcast.config.awayConfig.id = old.awayConfig.id;
    this.broadcast.config.awayConfig.shortName = old.awayConfig.shortName;
    this.broadcastsService.updateConfig(this.broadcast).then(() => {
      this.dialogRef.close('update');
    });
  }

  getFirstLetter(shortName: string): string {
    return shortName[0];
  }

  get awayColor(): UntypedFormControl {
    return this.formData.controls.awayColor as UntypedFormControl;
  }

  get homeColor(): UntypedFormControl {
    return this.formData.controls.homeColor as UntypedFormControl;
  }

  get awayLogoUrl(): UntypedFormControl {
    return this.formData.controls.awayLogoUrl as UntypedFormControl;
  }

  get homeLogoUrl(): UntypedFormControl {
    return this.formData.controls.homeLogoUrl as UntypedFormControl;
  }

  get statusMsg(): UntypedFormControl {
    return this.formData.controls.statusMsg as UntypedFormControl;
  }
}
